if (!customElements.get('zoom-dialog')) {
  class ZoomDialog extends HTMLElement {
    constructor() {
      super();
      this.dialog = this.querySelector('dialog');
      this.media = Array.from(this.querySelectorAll('.product-media-container'));
      this.thumbnails = this.querySelector('.dialog-thumbnails-list');
      this.highResImagesLoaded = new Set();

      this.handleScroll = this.debounce(this.handleScrollEvent.bind(this), 50);
    }

    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.dialog?.addEventListener('scroll', this.handleScroll, options);
      if (this.hasAttribute('data-enhanced-zoom')) {
        this.dialog?.addEventListener('click', event => {
          const wrapper = event.target.closest('drag-zoom-wrapper');
          if (!wrapper && !event.target.closest('[data-zoom-toggle]')) return;
          this.toggleZoom(wrapper || this.media[this.activeIndex]?.querySelector('drag-zoom-wrapper'), event);
        }, options);
        this.dialog?.addEventListener('close', () => this.resetZoom(), options);
      }
    }

    disconnectedCallback() {
      this.controller?.abort();
    }

    async open(index, event) {
      event?.preventDefault();
      if (!this.dialog || !Number.isInteger(index) || !this.media[index]) return;
      if (!this.dialog.open) {
        if (this.dialog.showModal) this.dialog.showModal();
        else this.dialog.setAttribute('open', '');
      }
      this.selectThumbnail(index, { behavior: 'auto' });
    }

    resetZoom() {
      this.querySelectorAll('drag-zoom-wrapper.is-zoomed').forEach(wrapper => {
        wrapper.classList.remove('is-zoomed');
        wrapper.scrollLeft = 0;
        wrapper.scrollTop = 0;
      });
      this.querySelector('[data-zoom-toggle]')?.setAttribute('aria-pressed', 'false');
    }

    toggleZoom(wrapper, event) {
      if (!wrapper) return;
      const rect = wrapper.getBoundingClientRect();
      const zoomed = wrapper.classList.toggle('is-zoomed');
      const fromImage = event.target.closest('drag-zoom-wrapper');
      wrapper.scrollLeft = zoomed ? (fromImage ? event.clientX - rect.left : rect.width / 2) : 0;
      wrapper.scrollTop = zoomed ? (fromImage ? event.clientY - rect.top : rect.height / 2) : 0;
      this.querySelector('[data-zoom-toggle]')?.setAttribute('aria-pressed', String(zoomed));
    }

    closeDialog() {
      if (this.dialog.close) {
        this.dialog.close();
      } else {
        this.dialog.removeAttribute('open');
      }
      window.dispatchEvent(new CustomEvent('dialog-close'));
    }

    close() {
      this.closeDialog();
    }

    async handleScrollEvent() {
      if (!this.dialog?.open || !this.media.length) return;
      const mostVisibleElement = await this.getMostVisibleElement(this.media);
      const activeIndex = this.media.indexOf(mostVisibleElement);
      if (this.activeIndex !== activeIndex) this.resetZoom();
      this.activeIndex = activeIndex;
      const targetThumbnail = this.thumbnails?.children[activeIndex];

      if (!targetThumbnail || !(targetThumbnail instanceof HTMLElement)) return;

      Array.from(this.thumbnails.querySelectorAll('button')).forEach((button, i) => {
        button.setAttribute('aria-selected', `${i === activeIndex}`);
      });

      this.loadHighResolutionImage(mostVisibleElement);
      this.dispatchEvent(new CustomEvent('zoom-media:selected', { detail: { index: activeIndex } }));
    }

    async handleThumbnailClick(index) {
      const behavior = this.prefersReducedMotion() ? 'auto' : 'smooth';
      this.selectThumbnail(index, { behavior });
    }

    async selectThumbnail(index, options = { behavior: 'smooth' }) {
      if (!Number.isInteger(index) || !this.media[index]) return;
      this.resetZoom();
      this.activeIndex = index;
      const targetThumbnail = this.thumbnails?.children[index];
      this.thumbnails?.querySelectorAll('button').forEach((button, i) => {
        button.setAttribute('aria-selected', String(i === index));
      });
      if (targetThumbnail) this.scrollIntoView(targetThumbnail, {
        ancestor: this.thumbnails, behavior: options.behavior, block: 'center', inline: 'center',
      });
      const targetImage = this.media[index];
      targetImage.scrollIntoView({ behavior: options.behavior, block: 'nearest', inline: 'start' });
      this.loadHighResolutionImage(targetImage);
      this.dispatchEvent(new CustomEvent('zoom-media-selected', { detail: { index } }));
    }

    loadHighResolutionImage(mediaContainer) {
      if (!mediaContainer.classList.contains('product-media-container--image')) return false;

      const image = mediaContainer.querySelector('img.product-media__image');
      if (!image || !(image instanceof HTMLImageElement)) return false;

      const highResolutionUrl = image.getAttribute('data_max_resolution');
      if (!highResolutionUrl || this.highResImagesLoaded.has(highResolutionUrl)) return false;

      this.preloadImage(highResolutionUrl);

      const newImage = new Image();
      newImage.className = image.className;
      newImage.alt = image.alt;
      newImage.setAttribute('data_max_resolution', highResolutionUrl);

      newImage.onload = () => {
        image.replaceWith(newImage);
        this.highResImagesLoaded.add(highResolutionUrl);
      };

      newImage.src = highResolutionUrl;
    }

    getMostVisibleElement(elements) {
      return new Promise((resolve) => {
        const observer = new IntersectionObserver(
          (entries) => {
            const mostVisible = entries.reduce((prev, current) =>
              current.intersectionRatio > prev.intersectionRatio ? current : prev
            );
            observer.disconnect();
            resolve(mostVisible.target);
          },
          { threshold: [0, 0.5, 1] }
        );
        for (const element of elements) {
          observer.observe(element);
        }
      });
    }

    debounce(func, wait) {
      let timeout;
      return function executedFunction(...args) {
        const later = () => {
          clearTimeout(timeout);
          func(...args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
      };
    }

    prefersReducedMotion() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    preloadImage(src) {
      const link = document.createElement('link');
      link.rel = 'preload';
      link.as = 'image';
      link.href = src;
      document.head.appendChild(link);
    }

    scrollIntoView(element, { ancestor, behavior = 'smooth', block = 'start', inline = 'start' } = {}) {
      if (!ancestor) {
        return element.scrollIntoView({ behavior, block, inline });
      }

      const ancestorRect = ancestor.getBoundingClientRect();
      const elementRect = element.getBoundingClientRect();

      const calculateScrollOffset = (alignment, ancestorStart, ancestorLength, elemStart, elemLength, currentScroll) => {
        switch (alignment) {
          case 'start':
            return currentScroll + elemStart - ancestorStart;
          case 'center':
            return currentScroll + elemStart - ancestorStart - ancestorLength / 2 + elemLength / 2;
          case 'end':
            return currentScroll + elemStart - ancestorStart - ancestorLength + elemLength;
          default:
            return currentScroll;
        }
      };

      const scrollTop =
        ancestor.scrollHeight > ancestor.clientHeight
          ? calculateScrollOffset(
              block,
              ancestorRect.top,
              ancestor.clientHeight,
              elementRect.top,
              elementRect.height,
              ancestor.scrollTop
          )
          : ancestor.scrollTop;

      const scrollLeft =
        ancestor.scrollWidth > ancestor.clientWidth
          ? Math.max(0, calculateScrollOffset(
              inline,
              ancestorRect.left,
              ancestor.clientWidth,
              elementRect.left,
              elementRect.width,
              ancestor.scrollLeft
            ))
          : ancestor.scrollLeft;

      ancestor.scrollTo({
        left: scrollLeft,
        top: scrollTop,
        behavior: behavior
      });
    }
  }

  if (!customElements.get('zoom-dialog')) {
    customElements.define('zoom-dialog', ZoomDialog);
  }
  document.addEventListener('click', e => {
    const trigger = e.target.closest('[data-zoom-target]');
    if (!trigger) return;

    const targetSelector = trigger.dataset.zoomTarget;
    const zoomDialog = document.querySelector(targetSelector);
    if (!zoomDialog) return;

    const index = Number(trigger.dataset.zoomIndex);

    if (trigger.classList.contains('product-media-container--zoomable')) {
      zoomDialog.open(index, e);
    }

    if (trigger.classList.contains('dialog-thumbnails-list__thumbnail')) {
      zoomDialog.handleThumbnailClick(index);

      const swiperThumbnail = zoomDialog.closest('media-gallery')?.querySelector(`.swiper-controls__thumbnail[data-index="${index}"]`);
      if (swiperThumbnail) {
        swiperThumbnail.click();
      }
    }
  });
}


if (!customElements.get('scroll-hint')) {
  class ScrollHint extends HTMLElement {
    connectedCallback() {
      this.addEventListener('scroll', theme.utils.rafThrottle(this.update.bind(this)));
      this.resizeObserver.observe(this);
    }

    disconnectedCallback() {
      this.removeEventListener('scroll', this.update);
      this.resizeObserver.disconnect();
    }

    update = () => {
      const { scrollTop, scrollHeight, clientHeight, scrollLeft, scrollWidth, clientWidth } = this;
      const scrollDirection = scrollWidth > clientWidth ? 'horizontal' : 'vertical';
      const scrollPercentage =
        scrollDirection === 'vertical'
          ? scrollTop / (scrollHeight - clientHeight)
          : scrollLeft / (scrollWidth - clientWidth);

      this.style.maskImage = Number.isNaN(scrollPercentage)
        ? ''
        : `linear-gradient(
          to ${scrollDirection === 'vertical' ? 'bottom' : 'right'},
          transparent ${scrollPercentage > 0 ? 1 : 0}%,
          black ${scrollPercentage < 0.1 ? scrollPercentage * 100 : 10}%,
          black ${scrollPercentage > 0.9 ? scrollPercentage * 100 : 90}%,
          transparent 100%
        )`;
    };

    resizeObserver = new ResizeObserver(this.update);
  }

  customElements.define('scroll-hint', ScrollHint);
}
